import {
  Box,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

import { getOrderClientFieldConfig } from "helpers/orderClientFields";
import {
  buildOrderArticleObservationWithAssociatedClient,
  formatPreparationDateLabel,
  getOrderPreparationArticleRemainingAmount,
  getOrderPreparationAssociatedClientLabel,
  getOrderPreparationCreatorLabel,
  getOrderPreparationObservation,
  getOrderPreparationStoreLabel,
  getOrderPreparationTotalBoxes,
  getOrderPreparationTotalUnits,
  sortOrderPreparationArticlesByLocation,
  sortOrdersForPreparationDetail,
} from "helpers/orderPreparationReports";
import { buildMovementArticleObservationSummary } from "helpers/movementArticleObservations";
import { useSession } from "providers/SessionProvider";
import { Order } from "types";

type Props = {
  orders: Order[];
};

export default function OrderPreparationDetailedPrint({ orders }: Props) {
  const { stores, users } = useSession();
  const sortedOrders = sortOrdersForPreparationDetail(orders);

  const printTableContainerSx = {
    mt: 1.5,
    "@media print": {
      overflow: "visible",
      breakInside: "auto",
      pageBreakInside: "auto",
    },
  } as const;

  const printTableSx = {
    "@media print": {
      tableLayout: "fixed",
      "& thead": {
        display: "table-header-group",
      },
      "& tfoot": {
        display: "table-footer-group",
      },
      "& tbody": {
        breakInside: "auto",
        pageBreakInside: "auto",
      },
      "& tr": {
        breakInside: "avoid",
        pageBreakInside: "avoid",
      },
      "& td, & th": {
        breakInside: "avoid",
        pageBreakInside: "avoid",
      },
    },
  } as const;

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      {sortedOrders.map((order, index) => {
        const clientFieldConfig = getOrderClientFieldConfig(order.client ?? null);
        const showLote = clientFieldConfig.detail.showLote;
        const showOc = clientFieldConfig.detail.showOc;
        const showFechaVencimiento =
          clientFieldConfig.detail.showFechaVencimiento;
        const showPeso = clientFieldConfig.detail.showPeso;
        const columnsCount =
          8 +
          (showLote ? 1 : 0) +
          (showOc ? 1 : 0) +
          (showFechaVencimiento ? 1 : 0) +
          (showPeso ? 1 : 0);
        const totalBoxes = getOrderPreparationTotalBoxes(order);
        const totalUnits = getOrderPreparationTotalUnits(order);
        const creatorLabel = getOrderPreparationCreatorLabel(order, users);
        const storeLabel = getOrderPreparationStoreLabel(order, stores);
        const observationLabel = getOrderPreparationObservation(order);
        const associatedClientLabel =
          getOrderPreparationAssociatedClientLabel(order);
        const sortedArticles = sortOrderPreparationArticlesByLocation(
          order.articles ?? [],
        );

        return (
          <Box
            key={Number(order.id)}
            sx={{
              mt: 1,
              pageBreakAfter: index === sortedOrders.length - 1 ? "auto" : "always",
              breakAfter: index === sortedOrders.length - 1 ? "auto" : "page",
            }}
          >
            <Paper
              sx={{
                border: "1px solid grey",
                borderRadius: "0.5rem",
                padding: "1rem",
                "@media print": {
                  breakInside: "auto",
                  pageBreakInside: "auto",
                },
              }}
            >
              <Typography variant="h6" mb={1.5}>
                Pedido #{order.id}
              </Typography>

              <Grid
                container
                spacing={2}
                mb={1.5}
                sx={{
                  "@media print": {
                    breakInside: "avoid",
                    pageBreakInside: "avoid",
                  },
                }}
              >
                <Grid item xs={4} display="flex" flexDirection="column" gap={1}>
                  <Typography variant="body2">
                    <b>Cliente:</b>{" "}
                    {order.client
                      ? `${order.client.code} - ${order.client.razon_social}`
                      : "-"}
                  </Typography>
                  <Typography variant="body2">
                    <b>Doc. Cliente:</b> {order.client_document || "-"}
                  </Typography>
                  <Typography variant="body2">
                    <b>Fecha Pedido:</b>{" "}
                    {formatPreparationDateLabel(order.movement_order?.received_at)}
                  </Typography>
                </Grid>

                <Grid item xs={4} display="flex" flexDirection="column" gap={1}>
                  <Typography variant="body2">
                    <b>Cliente Asociado:</b>{" "}
                    {order.movement_order?.associated_client?.razon_social ??
                      order.associated_client ??
                      "-"}
                  </Typography>
                  <Typography variant="body2">
                    <b>Vehículo:</b> {order.vehicle?.trim() || "-"}
                  </Typography>
                </Grid>

                <Grid item xs={4} display="flex" flexDirection="column" gap={1}>
                  <Typography variant="body2">
                    <b>Creado por:</b> {creatorLabel}
                  </Typography>
                  <Typography variant="body2">
                    <b>Depósito(s):</b> {storeLabel}
                  </Typography>
                </Grid>

                <Grid item xs={12} mt={1}>
                  <Typography variant="body2">
                    <b>Observaciones:</b> {observationLabel}
                  </Typography>
                </Grid>
              </Grid>

              <TableContainer component={Paper} variant="outlined" sx={printTableContainerSx}>
                <Table size="small" sx={printTableSx}>
                  <TableHead>
                    <TableRow>
                      <TableCell align="center">Código</TableCell>
                      <TableCell align="center">Descripción</TableCell>
                      {showLote && <TableCell align="center">Lote</TableCell>}
                      {showOc && <TableCell align="center">OC</TableCell>}
                      <TableCell align="center">Cantidad</TableCell>
                      <TableCell align="center">Cant. x Cont.</TableCell>
                      <TableCell align="center">Cant. Restante</TableCell>
                      <TableCell align="center">Subtotal</TableCell>
                      {showPeso && <TableCell align="center">Peso</TableCell>}
                      {showFechaVencimiento && (
                        <TableCell align="center">Fecha Venc.</TableCell>
                      )}
                      <TableCell align="center">Ubicación</TableCell>
                      <TableCell align="center">Observación</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedArticles.map((article) => (
                      <TableRow key={String(article.id)}>
                        <TableCell align="center">
                          {article.code ?? article.article_locations?.article?.code ?? "-"}
                        </TableCell>
                        <TableCell align="center">
                          {article.description ??
                            article.article_locations?.article?.description ??
                            "-"}
                        </TableCell>
                        {showLote && (
                          <TableCell align="center">
                            {article.lote ?? article.article_locations?.lote ?? "-"}
                          </TableCell>
                        )}
                        {showOc && (
                          <TableCell align="center">
                            {article.oc ?? article.article_locations?.oc ?? "-"}
                          </TableCell>
                        )}
                        <TableCell align="center">{article.amount ?? 0}</TableCell>
                        <TableCell align="center">
                          {article.article_locations?.amount_per_container ??
                            article.amount_per_container ??
                            0}
                        </TableCell>
                        <TableCell align="center">
                          {getOrderPreparationArticleRemainingAmount(article)}
                        </TableCell>
                        <TableCell align="center">{article.subtotal ?? 0}</TableCell>
                        {showPeso && (
                          <TableCell align="center">{article.subtotal_kg ?? 0}</TableCell>
                        )}
                        {showFechaVencimiento && (
                          <TableCell align="center">
                            {formatPreparationDateLabel(
                              article.fecha_vencimiento ??
                                article.article_locations?.fecha_vencimiento,
                            )}
                          </TableCell>
                        )}
                        <TableCell align="center">
                          {article.article_locations?.location?.code ?? "-"}
                        </TableCell>
                        <TableCell align="center">
                          {buildOrderArticleObservationWithAssociatedClient(
                            buildMovementArticleObservationSummary(article),
                            associatedClientLabel,
                          )}
                        </TableCell>
                      </TableRow>
                    ))}

                    {sortedArticles.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={columnsCount} align="center">
                          No hay mercadería para mostrar.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {(order.additional_items ?? []).length > 0 && (
                <TableContainer component={Paper} variant="outlined" sx={{ mt: 1.5 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell align="center">Adicional</TableCell>
                        <TableCell align="center">Cantidad</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(order.additional_items ?? []).map((additionalItem) => (
                        <TableRow key={String(additionalItem.id)}>
                          <TableCell align="center">
                            {additionalItem.description ??
                              additionalItem.additional_item?.description ??
                              "-"}
                          </TableCell>
                          <TableCell align="center">
                            {additionalItem.amount ?? 0}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              <Paper
                sx={{
                  border: "1px solid grey",
                  p: 1,
                  mt: 2,
                  display: "flex",
                  justifyContent: "flex-end",
                }}
              >
                <Box display="flex" gap={4}>
                  <Typography variant="body2">
                    <b>Cantidad Total:</b> {totalBoxes}
                  </Typography>
                  <Typography variant="body2">
                    <b>Total Unidad:</b> {totalUnits}
                  </Typography>
                </Box>
              </Paper>
            </Paper>
          </Box>
        );
      })}
    </Box>
  );
}
